import { io } from "socket.io-client";
import { tokenManager } from "../Api/tokenManager";
import { getSocket } from "../Api/socket";

/**
 * @param {import("socket.io-client").Socket} socket
 * @param {{ onMessage: (msg: object) => void, onJoined?: () => void, onError?: (m: string) => void, onHistory?: (payload: object) => void, onAdminRead?: (payload: object) => void }} handlers
 * @returns {() => void} cleanup (remove listeners only; does not disconnect shared socket)
 */
function bindSupportSocket(socket, handlers) {
  const { onMessage, onJoined, onError, onHistory, onAdminRead } = handlers;

  const emitJoin = () => {
    try {
      socket.emit("support:join");
    } catch (_) {}
  };

  const onConnect = () => emitJoin();

  const onSupportMsg = (msg) => {
    if (msg && typeof msg === "object" && msg.text != null) onMessage(msg);
  };

  const onAdminReadEvt = (payload) => {
    try {
      onAdminRead?.(payload);
    } catch (_) {}
  };

  const onJoinedEvt = () => {
    try {
      onJoined?.();
    } catch (_) {}
  };

  const onConnectErr = () => {
    try {
      onError?.("Could not connect to support. Check your network and try again.");
    } catch (_) {}
  };

  const onHistoryEvt = (payload) => {
    try {
      onHistory?.(payload);
    } catch (_) {}
  };

  socket.on("connect", onConnect);
  socket.on("support:message", onSupportMsg);
  socket.on("support:joined", onJoinedEvt);
  socket.on("support:history", onHistoryEvt);
  socket.on("support:read", onAdminReadEvt);
  socket.on("connect_error", onConnectErr);

  if (socket.connected) emitJoin();

  return () => {
    try {
      socket.off("connect", onConnect);
      socket.off("support:message", onSupportMsg);
      socket.off("support:joined", onJoinedEvt);
      socket.off("support:history", onHistoryEvt);
      socket.off("support:read", onAdminReadEvt);
      socket.off("connect_error", onConnectErr);
    } catch (_) {}
  };
}

/**
 * Live care-team channel: dedicated env URL+token, else shared patient API socket.
 * @returns {{ socket: import("socket.io-client").Socket | null, cleanup: () => void, mode: "dedicated" | "main" | "none" }}
 */
export function connectPatientSupportChannel(handlers) {
  const url = String(process.env.REACT_APP_SUPPORT_SOCKET_URL || "").trim();
  const token = String(process.env.REACT_APP_SUPPORT_SOCKET_TOKEN || "").trim();

  if (url && token) {
    const socket = io(url, {
      transports: ["websocket", "polling"],
      auth: { token },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 500,
    });
    const unbind = bindSupportSocket(socket, handlers);
    return {
      socket,
      mode: "dedicated",
      cleanup: () => {
        try {
          unbind();
        } catch (_) {}
        try {
          socket.removeAllListeners();
          socket.disconnect();
        } catch (_) {}
      },
    };
  }

  const patientTok = tokenManager.getTokenForRole("patient");
  if (!patientTok) {
    return { socket: null, mode: "none", cleanup: () => {} };
  }

  const socket = getSocket({ role: "patient" });
  if (!socket) {
    return { socket: null, mode: "none", cleanup: () => {} };
  }

  const unbind = bindSupportSocket(socket, handlers);
  return {
    socket,
    mode: "main",
    cleanup: () => {
      try {
        unbind();
      } catch (_) {}
    },
  };
}

export function emitPatientSupportMessage(socket, text) {
  if (socket && typeof socket.emit === "function") {
    socket.emit("support:message", { text: String(text || "").trim() });
  }
}

/** Patient → admins, or admins → patient room (admin must include patientId in payload on server). */
export function emitPatientSupportTyping(socket, isTyping) {
  if (socket && typeof socket.emit === "function") {
    socket.emit("support:typing", { isTyping: !!isTyping });
  }
}

/** Tell the care team you have read through this admin message (read receipts for admins). */
export function emitPatientSupportRead(socket, lastMessageId) {
  if (socket && typeof socket.emit === "function" && lastMessageId) {
    socket.emit("support:patientRead", {
      lastMessageId: String(lastMessageId),
      readAt: new Date().toISOString(),
    });
  }
}

/** Admin: load persisted thread for a patient (requires admin JWT). */
export function emitSupportFetchHistory(socket, patientId, limit = 200) {
  if (socket && typeof socket.emit === "function" && patientId) {
    socket.emit("support:fetchHistory", { patientId: String(patientId), limit });
  }
}

/** @returns {boolean} */
export function hasDedicatedSupportEnv() {
  const u = String(process.env.REACT_APP_SUPPORT_SOCKET_URL || "").trim();
  const t = String(process.env.REACT_APP_SUPPORT_SOCKET_TOKEN || "").trim();
  return Boolean(u && t);
}
